import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Navigation, Save, Target } from "lucide-react";
import { toast } from "sonner";

const emptyRow = () => ({ base_lat: 0, base_lng: 0, radius_m: 500, mode: "flexible", on_time_cutoff: "09:30", enabled: true });

export default function GeoFenceConfig() {
  const [ests, setEsts] = useState([]);
  const [fences, setFences] = useState({});
  const [sel, setSel] = useState("__org__");
  const [working, setWorking] = useState(false);

  const load = async () => {
    try {
      const [e, f] = await Promise.all([api.get("/establishments").catch(() => ({ data: [] })), api.get("/geo-fence")]);
      const list = Array.isArray(e.data) ? e.data : (e.data?.rows || []);
      setEsts([{ id: "__org__", name: "Organisation (default)" }, ...list]);
      const map = {};
      (f.data || []).forEach(x => { map[x.establishment_id] = x; });
      setFences(map);
    } catch (err) { toast.error(formatErr(err)); }
  };
  useEffect(() => { load(); }, []);

  const cur = fences[sel] || emptyRow();
  const update = (k, v) => setFences(f => ({ ...f, [sel]: { ...(f[sel] || emptyRow()), [k]: v } }));

  const save = async () => {
    setWorking(true);
    try {
      const body = { ...cur, base_lat: Number(cur.base_lat), base_lng: Number(cur.base_lng),
                     radius_m: Number(cur.radius_m) || 500 };
      await api.put(`/geo-fence/${sel}`, body);
      toast.success("Geo-fence saved");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setWorking(false); }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Browser does not support GPS");
    navigator.geolocation.getCurrentPosition(
      p => { update("base_lat", p.coords.latitude); update("base_lng", p.coords.longitude); toast.success("Base location set from device GPS"); },
      () => toast.error("Could not get location — enable location access")
    );
  };

  return (
    <div className="p-8 max-w-3xl">
      <div className="mb-6">
        <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Navigation className="w-3 h-3"/> HRMS · Settings</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Geo Fence Configuration</h1>
        <div className="text-xs text-muted-foreground mt-1">Base location + allowed radius for geo-attendance, per establishment.</div>
      </div>

      <div className="border border-border bg-card rounded-sm p-4 space-y-4" data-testid="geo-fence-form">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Establishment</Label>
          <Select value={sel} onValueChange={setSel}>
            <SelectTrigger data-testid="gfc-est" className="mt-1 rounded-sm bg-secondary border-border h-9"><SelectValue/></SelectTrigger>
            <SelectContent>{ests.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Base Latitude</Label>
            <Input data-testid="gfc-lat" type="number" step="any" value={cur.base_lat} onChange={(e) => update("base_lat", e.target.value)} className="mt-1 rounded-sm bg-secondary border-border h-9 font-mono"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Base Longitude</Label>
            <Input data-testid="gfc-lng" type="number" step="any" value={cur.base_lng} onChange={(e) => update("base_lng", e.target.value)} className="mt-1 rounded-sm bg-secondary border-border h-9 font-mono"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Allowed Radius (metres)</Label>
            <Input data-testid="gfc-radius" type="number" min="10" value={cur.radius_m} onChange={(e) => update("radius_m", e.target.value)} className="mt-1 rounded-sm bg-secondary border-border h-9 font-mono"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">On-time cutoff (HH:MM)</Label>
            <Input data-testid="gfc-cutoff" type="time" value={cur.on_time_cutoff} onChange={(e) => update("on_time_cutoff", e.target.value)} className="mt-1 rounded-sm bg-secondary border-border h-9"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Validation Mode</Label>
            <Select value={cur.mode} onValueChange={(v) => update("mode", v)}>
              <SelectTrigger data-testid="gfc-mode" className="mt-1 rounded-sm bg-secondary border-border h-9"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="strict">Strict (block out-of-range)</SelectItem>
                <SelectItem value="flexible">Flexible (flag as Out of Range)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Enabled</Label>
            <Select value={cur.enabled ? "yes" : "no"} onValueChange={(v) => update("enabled", v === "yes")}>
              <SelectTrigger data-testid="gfc-enabled" className="mt-1 rounded-sm bg-secondary border-border h-9"><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="yes">Yes</SelectItem><SelectItem value="no">No</SelectItem></SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button data-testid="gfc-use-location" variant="outline" onClick={useMyLocation} className="rounded-sm text-xs uppercase tracking-wider"><Target className="w-3 h-3 mr-1"/> Use My Current Location</Button>
          <Button data-testid="gfc-save" onClick={save} disabled={working} className="rounded-sm text-xs uppercase tracking-wider"><Save className="w-3 h-3 mr-1"/> {working ? "Saving…" : "Save"}</Button>
        </div>
      </div>
    </div>
  );
}
